/**
 * Everything the app needs to know about the deployed stack.
 *
 * Checked in rather than injected at build time. None of it is secret: the user pool id and client id
 * are public by design in a browser OAuth flow, and the API is an unauthenticated hostname behind an
 * authorizer that rejects everything without a credential. A build that needs a secrets file is a
 * build that breaks the first time someone clones the repo.
 *
 * Regenerate after any infrastructure change:
 *
 * ```
 * cd infra && AWS_PROFILE=mrg terraform output
 * ```
 */
export const config = {
  apiBaseUrl: "https://fzx5n5g553.execute-api.us-west-2.amazonaws.com",

  cognito: {
    /** No client secret, and there must never be one — a secret in a browser is not a secret. */
    clientId: "5vd47rg7j8fp5ktlpotu01pgqb",
    hostedUiDomain: "grayson-earl-jam-dev.auth.us-west-2.amazoncognito.com",
    /**
     * `openid` is what makes this an OIDC flow at all; `email` and `profile` populate the user's name
     * and address.
     *
     * `aws.cognito.signin.user.admin` is required, not optional. The API's authorizer verifies an
     * access token by calling Cognito's `GetUser`, and `GetUser` rejects a token that does not carry
     * this scope — so without it sign-in succeeds and then every API call returns 403. It must also be
     * listed in `allowed_oauth_scopes` on the app client, or Cognito refuses to issue it; both halves
     * are needed and each is useless alone.
     *
     * Deliberately no custom scopes beyond that. The API authorizes on team membership, re-checked on
     * every request, and never on anything encoded in the token.
     */
    scopes: ["openid", "email", "profile", "aws.cognito.signin.user.admin"],
  },

  /**
   * The realtime channel. `host` in the WebSocket authorization subprotocol names the **HTTP**
   * endpoint even though the socket opens against the realtime one — AppSync validates the connection
   * against the HTTP host, and getting this the wrong way round produces a handshake failure with no
   * useful message.
   */
  events: {
    httpDomain: "mxzmbtzjozh3xhdeqllcbjjfwe.appsync-api.us-west-2.amazonaws.com",
    realtimeDomain: "mxzmbtzjozh3xhdeqllcbjjfwe.appsync-realtime-api.us-west-2.amazonaws.com",
  },
} as const;

/**
 * Where Cognito sends the browser back to.
 *
 * Derived from the current origin rather than hard-coded so that the dev server and the deployed site
 * each redirect to themselves. Both origins are registered as callback URLs by `web_app_origins` in
 * Terraform; an origin that is not in that list fails at Cognito with `redirect_mismatch`.
 *
 * No path, only the origin, because the list in Terraform contains bare origins and Cognito matches
 * the redirect URI exactly.
 */
export function redirectUri(): string {
  return window.location.origin;
}
